import { useMemo, useState } from 'react';

import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  GitBranch,
  Info,
  Save,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';

import {
  AVISO_DE_ACAO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { PageHeader } from '@/ui/PageHeader';
import { Spinner } from '@/ui/Spinner';
import { Alert, AlertDescription, AlertTitle } from '@/ui/base/alert';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { Switch } from '@/ui/base/switch';
import { Textarea } from '@/ui/base/textarea';

import {
  NIVEIS_DE_ASSINATURA,
  ROTULO_DO_NIVEL,
  ROTULO_DO_SELO,
  ROTULO_DO_TIPO,
  SELOS_GOVBR,
  usePoliticasDeAssinatura,
  useProvedoresAceitos,
  validarPolitica,
  type NivelDeAssinatura,
  type PoliticaDeAssinatura,
  type ProvedorAceito,
  type SeloGovBr,
  type SituacaoDaPolitica,
} from './dados';

/** Valor sentinela do campo de selo: "esta política não exige selo gov.br". */
const SEM_SELO = 'nenhum';

const SELO_DA_SITUACAO: Record<
  SituacaoDaPolitica,
  { rotulo: string; variante: 'default' | 'secondary' | 'destructive'; Icone: typeof CheckCircle2 }
> = {
  valida: { rotulo: 'Válida', variante: 'default', Icone: CheckCircle2 },
  aviso: { rotulo: 'Com avisos', variante: 'secondary', Icone: AlertTriangle },
  invalida: { rotulo: 'Inválida', variante: 'destructive', Icone: XCircle },
};

/**
 * `/admin/parametros/politica-assinatura` — qual nível jurídico de assinatura
 * cada tipo de documento exige para ter validade legal (Lei 14.063/2020).
 *
 * O DIAGNÓSTICO FUNCIONA DE VERDADE; O "SALVAR" NÃO. Cruzar a política com os
 * provedores ativos e dizer se a combinação fecha é regra de negócio pura, que
 * roda no navegador — mesmo caso da régua de alçadas. E é o que dá valor à tela:
 * uma política que exige assinatura qualificada sem nenhum provedor ICP-Brasil
 * ativo é um documento que ninguém consegue assinar, e o defeito só aparece
 * quando alguém cruza as duas parametrizações. O diagnóstico recalcula enquanto
 * o usuário edita, antes mesmo de tentar salvar.
 *
 * Já gravar precisaria de `/politica-assinatura`, que não existe na API: a
 * tabela `politica_assinatura_documento` (migração `0002_dominio_base.sql`) não
 * tem política de escrita. O botão mostra o aviso honesto de indisponibilidade
 * (`modulos/operacao/comum/cadastroIndisponivel.ts`).
 */
export function PoliticaAssinaturaPage() {
  const politicas = usePoliticasDeAssinatura();
  const provedores = useProvedoresAceitos();

  const carregando = politicas.isLoading || provedores.isLoading;
  const comErro = politicas.isError || provedores.isError;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Política de assinatura digital"
        description="Define o nível jurídico de assinatura que cada tipo de documento exige para ter validade legal, conforme a Lei 14.063/2020."
      >
        <Button asChild variant="outline" size="sm">
          <Link to="/admin/parametros/fluxo-assinatura">
            <GitBranch className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            Como funciona o fluxo
          </Link>
        </Button>
      </PageHeader>

      <Alert>
        <Info className="h-4 w-4" aria-hidden="true" />
        <AlertTitle>Três parametrizações que se completam</AlertTitle>
        <AlertDescription className="text-xs">
          Esta tela define o <strong>nível jurídico</strong> que o documento exige. Qual{' '}
          <strong>certificado</strong> pode ser usado fica em{' '}
          <Link to="/admin/parametros/provedores-assinatura" className="font-medium underline">
            Provedores de assinatura
          </Link>
          , e o selo exigido para <strong>entrar</strong> no sistema fica em{' '}
          <Link to="/admin/parametros/govbr-operacoes" className="font-medium underline">
            Operações gov.br
          </Link>
          .
        </AlertDescription>
      </Alert>

      {comErro ? (
        <ErrorState
          variant="load-failed"
          description="Não foi possível carregar as políticas de assinatura agora. Verifique sua conexão e tente novamente."
          primaryAction={{
            label: 'Tentar de novo',
            onClick: () => {
              void politicas.refetch();
              void provedores.refetch();
            },
          }}
        />
      ) : carregando ? (
        <Spinner />
      ) : !politicas.data || politicas.data.length === 0 ? (
        <EmptyState
          icon={ShieldCheck}
          title="Nenhuma política cadastrada"
          description="Sem política, nenhum tipo de documento tem nível de assinatura definido — e nada pode ser assinado com validade legal."
        />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {politicas.data.map((politica) => (
            <CartaoDaPolitica
              key={politica.id}
              politica={politica}
              provedores={(provedores.data ?? []).filter(
                (provedor) => provedor.politica_id === politica.id,
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface CartaoDaPoliticaProps {
  politica: PoliticaDeAssinatura;
  provedores: ProvedorAceito[];
}

function CartaoDaPolitica({ politica, provedores }: CartaoDaPoliticaProps) {
  const [nivel, setNivel] = useState<NivelDeAssinatura>(politica.nivel_minimo);
  const [selo, setSelo] = useState<string>(politica.selo_govbr_minimo ?? SEM_SELO);
  const [baseLegal, setBaseLegal] = useState(politica.base_legal);
  const [observacoes, setObservacoes] = useState(politica.observacoes ?? '');
  const [ativo, setAtivo] = useState(politica.ativo);

  const seloEscolhido: SeloGovBr | null = selo === SEM_SELO ? null : (selo as SeloGovBr);

  const alterado =
    nivel !== politica.nivel_minimo ||
    seloEscolhido !== politica.selo_govbr_minimo ||
    baseLegal !== politica.base_legal ||
    observacoes !== (politica.observacoes ?? '') ||
    ativo !== politica.ativo;

  /**
   * O diagnóstico roda sobre a política COMO ESTÁ NA TELA, não como está no
   * servidor — é o que permite ver o efeito de uma mudança antes de tentar
   * salvá-la.
   */
  const validacao = useMemo(
    () =>
      validarPolitica(
        { ...politica, nivel_minimo: nivel, selo_govbr_minimo: seloEscolhido, ativo },
        provedores,
      ),
    [politica, nivel, seloEscolhido, ativo, provedores],
  );

  const { rotulo, variante, Icone } = SELO_DA_SITUACAO[validacao.situacao];

  function salvar() {
    if (!baseLegal.trim()) {
      toast.error('Base legal obrigatória', {
        description: 'Toda política precisa citar a norma que a fundamenta.',
      });
      return;
    }
    // A tabela `politica_assinatura_documento` não aceita escrita hoje — não há
    // rota na API nem política de escrita no banco. O aviso é honesto.
    mostrarAviso(AVISO_DE_ACAO_INDISPONIVEL);
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="h-4 w-4 text-primary" aria-hidden="true" />
            {ROTULO_DO_TIPO[politica.tipo_documento]}
          </CardTitle>
          <Badge variant={variante} className="gap-1">
            <Icone className="h-3 w-3" aria-hidden="true" />
            {rotulo}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor={`nivel-${politica.id}`} className="text-xs">
              Nível mínimo de assinatura
            </Label>
            <Select value={nivel} onValueChange={(valor) => setNivel(valor as NivelDeAssinatura)}>
              <SelectTrigger id={`nivel-${politica.id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {NIVEIS_DE_ASSINATURA.map((item) => (
                  <SelectItem key={item} value={item}>
                    {ROTULO_DO_NIVEL[item]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={`selo-${politica.id}`} className="text-xs">
              Selo gov.br mínimo
            </Label>
            <Select value={selo} onValueChange={setSelo}>
              <SelectTrigger id={`selo-${politica.id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={SEM_SELO}>Não exige</SelectItem>
                {SELOS_GOVBR.map((item) => (
                  <SelectItem key={item} value={item}>
                    {ROTULO_DO_SELO[item]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor={`base-${politica.id}`} className="text-xs">
            Base legal
          </Label>
          <Input
            id={`base-${politica.id}`}
            value={baseLegal}
            onChange={(evento) => setBaseLegal(evento.target.value)}
            placeholder="Lei 14.063/2020, art. 5º, II"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor={`obs-${politica.id}`} className="text-xs">
            Observações
          </Label>
          <Textarea
            id={`obs-${politica.id}`}
            rows={2}
            value={observacoes}
            onChange={(evento) => setObservacoes(evento.target.value)}
            placeholder="Quando esta política se aplica."
          />
        </div>

        <div className="flex items-center justify-between rounded-lg border bg-muted/30 p-3">
          <Label htmlFor={`ativo-${politica.id}`} className="cursor-pointer">
            <span className="text-sm font-medium">Política ativa</span>
            <span className="mt-0.5 block text-xs font-normal text-muted-foreground">
              Inativa, o documento não pode ser assinado por nenhum caminho.
            </span>
          </Label>
          <Switch id={`ativo-${politica.id}`} checked={ativo} onCheckedChange={setAtivo} />
        </div>

        <Alert variant={validacao.situacao === 'invalida' ? 'destructive' : 'default'}>
          <Icone className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>Diagnóstico</AlertTitle>
          <AlertDescription className="space-y-1 text-xs">
            {validacao.mensagens.map((mensagem) => (
              <div key={mensagem}>{mensagem}</div>
            ))}
          </AlertDescription>
        </Alert>

        <div className="flex justify-end border-t pt-3">
          <Button size="sm" onClick={salvar} disabled={!alterado}>
            <Save className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            Salvar
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default PoliticaAssinaturaPage;
